/**
 * DocViewer — Inline doc reader that renders markdown content.
 */
import { createElement, useMemo } from 'react';
import { renderMarkdown } from '../../utils/markdown';
import type { DocSection, DocSearchResult } from '../../types';

interface DocViewerProps {
  /** The doc section to render (looked up from search result) */
  section: DocSection | null;
  /** Fallback: render from search result snippet if section not found */
  searchResult?: DocSearchResult;
  onBack: () => void;
}

export function DocViewer({ section, searchResult, onBack }: DocViewerProps) {
  const title = section?.title ?? searchResult?.title ?? 'Document';
  const content = section?.content ?? searchResult?.snippet ?? '';

  const rendered = useMemo(() => renderMarkdown(content), [content]);

  return createElement(
    'div',
    {
      style: {
        display: 'flex',
        flexDirection: 'column' as const,
        height: '100%',
        overflow: 'hidden',
      },
    },
    // Back button + title
    createElement(
      'div',
      {
        style: {
          display: 'flex',
          alignItems: 'center',
          padding: '8px 0 12px',
          borderBottom: '1px solid var(--sw-border)',
          marginBottom: '12px',
          flexShrink: 0,
        },
      },
      createElement(
        'button',
        {
          type: 'button',
          onClick: onBack,
          'aria-label': 'Back to results',
          style: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '48px',
            height: '48px',
            minWidth: '48px',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            color: 'var(--sw-accent-primary)',
            padding: 0,
          },
        },
        createElement(
          'svg',
          {
            width: 20,
            height: 20,
            viewBox: '0 0 24 24',
            fill: 'none',
            stroke: 'currentColor',
            strokeWidth: 2,
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
          },
          createElement('polyline', { points: '15 18 9 12 15 6' }),
        ),
      ),
      createElement(
        'h2',
        {
          style: {
            margin: 0,
            fontSize: '16px',
            fontWeight: 600,
            color: 'var(--sw-text-primary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap' as const,
          },
        },
        title,
      ),
    ),
    // Content
    createElement(
      'div',
      {
        style: {
          flex: 1,
          overflowY: 'auto' as const,
          color: 'var(--sw-text-primary)',
          fontSize: '14px',
          lineHeight: 1.6,
          padding: '0 4px',
        },
      },
      ...rendered,
    ),
  );
}
